/**
 * Customer-facing "pay this link" page — no login required. Reached via a
 * real SMS payment request (RequestPaymentModal), landing here at
 * app.html?payLink=1&appointmentId=... (same unauthenticated-entry pattern
 * as WidgetEmbedBookingScreen). Fetches the invoice from the backend (a
 * signed-out customer can't read Firestore directly — appointments require
 * auth per firestore.rules) and pays via whichever of Stripe/PayPal the
 * business has connected, reusing the same real payment components already
 * built for the main booking flow.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, TouchableOpacity, ScrollView, Platform, Linking } from 'react-native';
import WebStripeCheckoutModal, { type WebStripeCheckoutResult } from '../components/WebStripeCheckoutModal';
import PayPalSmartButton from '../components/PayPalSmartButton';
import { buildPayPalPaymentUrl } from '../utils/paypalDirectPayment';
import { WIDGET_BOOKING_API } from '../utils/widgetEmbed';

interface InvoiceLineItem {
  name: string;
  quantity: number;
  unitPrice: number;
}

interface InvoiceData {
  businessId: string;
  businessName: string;
  customerName: string;
  lineItems: InvoiceLineItem[];
  total: number;
  status: 'paid' | 'pending';
  stripeReady: boolean;
  paypalConfigured: boolean;
  paypalClientId?: string | null;
  paypalDirectUrl?: string | null;
}

function getAppointmentIdFromUrl(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('appointmentId');
  } catch {
    return null;
  }
}

const PayLinkScreen: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);

  const appointmentId = getAppointmentIdFromUrl();

  useEffect(() => {
    if (!appointmentId) {
      setError('Missing payment link details.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${WIDGET_BOOKING_API}/appointments/${encodeURIComponent(appointmentId)}/invoice`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.error || 'Could not load this invoice.');
          setLoading(false);
          return;
        }
        setInvoice(data);
        if (data.status === 'paid') setPaid(true);
      } catch (e) {
        setError('Could not reach the payment server. Check your connection and try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [appointmentId]);

  const markPaid = async (body: Record<string, unknown>) => {
    if (!appointmentId) return;
    setPaying(true);
    try {
      const res = await fetch(`${WIDGET_BOOKING_API}/appointments/${encodeURIComponent(appointmentId)}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPaid(true);
      } else {
        setError(data.error || 'Payment could not be confirmed. Please contact the business.');
      }
    } catch {
      setError('Payment may have gone through, but we could not confirm it. Please contact the business.');
    } finally {
      setPaying(false);
    }
  };

  const handleStripeResult = async (result: WebStripeCheckoutResult) => {
    setShowStripeModal(false);
    if (result.success && result.paymentIntentId) {
      await markPaid({ paymentMethod: 'stripe', paymentIntentId: result.paymentIntentId });
    } else if (!result.cancelled) {
      setError(result.error || 'Payment failed. Please try again.');
    }
  };

  // Off-platform PayPal link — BookAPT has no capture ID to verify here, so
  // (unlike the Stripe/PayPal-API paths above) this can't auto-confirm.
  // The merchant confirms payment themselves once it lands in their PayPal.
  const handlePayPalDirect = async () => {
    if (!invoice?.paypalDirectUrl) return;
    const url = buildPayPalPaymentUrl(invoice.paypalDirectUrl, invoice.total);
    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(url, '_blank');
      } else {
        await Linking.openURL(url);
      }
      setAwaitingConfirmation(true);
    } catch {
      setError('Could not open PayPal. Please try again.');
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#007AFF" />
      </View>
    );
  }

  if (error && !invoice) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!invoice) return null;

  if (paid) {
    return (
      <View style={styles.center}>
        <Text style={styles.successTitle}>✅ Payment received</Text>
        <Text style={styles.successText}>
          Thank you! Your payment of ${invoice.total.toFixed(2)} to {invoice.businessName} is complete.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.businessName}>{invoice.businessName}</Text>
      <Text style={styles.subtitle}>Payment request for {invoice.customerName}</Text>

      <View style={styles.card}>
        {invoice.lineItems.map((item, i) => (
          <View key={i} style={styles.itemRow}>
            <Text style={styles.itemName}>
              {item.name}{item.quantity > 1 ? ` × ${item.quantity}` : ''}
            </Text>
            <Text style={styles.itemAmount}>${(item.unitPrice * item.quantity).toFixed(2)}</Text>
          </View>
        ))}
        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>Total</Text>
          <Text style={styles.totalValue}>${invoice.total.toFixed(2)}</Text>
        </View>
      </View>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      {awaitingConfirmation ? (
        <View>
          <Text style={styles.successTitle}>Almost done</Text>
          <Text style={styles.successText}>
            Complete your payment in the PayPal tab that just opened. {invoice.businessName} will confirm your
            payment shortly — this page won't update automatically.
          </Text>
        </View>
      ) : paying ? (
        <ActivityIndicator size="small" color="#007AFF" style={styles.payingSpinner} />
      ) : (
        <View style={styles.paymentOptions}>
          {invoice.stripeReady && (
            <TouchableOpacity style={styles.payButton} onPress={() => setShowStripeModal(true)}>
              <Text style={styles.payButtonText}>Pay ${invoice.total.toFixed(2)} with Card</Text>
            </TouchableOpacity>
          )}
          {!invoice.paypalConfigured && invoice.paypalDirectUrl && (
            <TouchableOpacity style={[styles.payButton, styles.paypalDirectButton]} onPress={handlePayPalDirect}>
              <Text style={styles.payButtonText}>Pay ${invoice.total.toFixed(2)} with PayPal</Text>
            </TouchableOpacity>
          )}
          {invoice.paypalConfigured && invoice.paypalClientId && (
            <PayPalSmartButton
              clientId={invoice.paypalClientId}
              amountUsd={invoice.total}
              createOrder={async () => {
                const { createPaypalOrder } = await import('../services/paypalOrdersService');
                const result = await createPaypalOrder(invoice.businessId, invoice.total);
                if (!result.success || !result.orderId) {
                  throw new Error(result.error || 'Could not start PayPal checkout.');
                }
                return result.orderId;
              }}
              onApprove={async (orderId) => {
                const { capturePaypalOrder } = await import('../services/paypalOrdersService');
                const capture = await capturePaypalOrder(invoice.businessId, orderId);
                if (!capture.success) {
                  return { success: false, error: capture.error || 'PayPal payment was not completed.' };
                }
                await markPaid({
                  paymentMethod: 'paypal',
                  businessId: invoice.businessId,
                  captureId: capture.captureId,
                  orderId: capture.orderId,
                });
                return { success: true };
              }}
              onError={(message) => setError(message)}
            />
          )}
          {!invoice.stripeReady && !invoice.paypalConfigured && !invoice.paypalDirectUrl && (
            <Text style={styles.errorText}>This business hasn't set up online payments yet. Please contact them directly.</Text>
          )}
        </View>
      )}

      {invoice.stripeReady && (
        <WebStripeCheckoutModal
          visible={showStripeModal}
          intentInput={{
            businessId: invoice.businessId,
            amount: invoice.total,
            serviceName: invoice.lineItems.map((i) => i.name).join(', '),
          }}
          onClose={() => setShowStripeModal(false)}
          onResult={handleStripeResult}
        />
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  container: {
    flexGrow: 1,
    padding: 24,
    maxWidth: 480,
    width: '100%',
    alignSelf: 'center',
  },
  businessName: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1C1C1E',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 24,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E1E5E9',
    padding: 16,
    marginBottom: 20,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  itemName: {
    fontSize: 15,
    color: '#333',
    flex: 1,
  },
  itemAmount: {
    fontSize: 15,
    color: '#333',
    fontWeight: '600',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
  },
  totalLabel: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  totalValue: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  paymentOptions: {
    gap: 12,
  },
  payButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  payButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  paypalDirectButton: {
    backgroundColor: '#003087',
  },
  payingSpinner: {
    marginTop: 16,
  },
  errorText: {
    color: '#c00',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
  },
  successTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  successText: {
    fontSize: 15,
    color: '#666',
    textAlign: 'center',
  },
});

export default PayLinkScreen;
